/**
 * The set table — the 02.08 KANON gallery's own, as a real `<table>`.
 *
 * ── A form, not a data grid ──────────────────────────────────────────────────────────────────
 * `<th scope="col">` and `<th scope="row">`, so a screen reader announces "Ciężar, seria 3" from
 * the markup rather than from a hand-written label. What it deliberately does NOT get is a roving
 * tab stop: every cell here is a focusable text input, so each of them is a real tab stop, and a
 * single one would take that away. The view ADR's grid semantics belong to the CYCLE MATRIX, where
 * cells are values rather than fields.
 *
 * It DOES get spreadsheet navigation, added 3 August 2026 on the owner's ask and completed on 8
 * September 2026 with the mock's own Enter and Tab. No tabindex moves and no focus is trapped: an
 * arrow is intercepted only where the browser's own answer inside a text input would have been
 * "nothing happens", Enter is the mock's `ArrowDown`, and Tab walks the VALUE cells — skipping the
 * type badge and the two row buttons the browser would have stopped at between every pair — while
 * still handing the key back at the two ends of the grid so the table can be left. The whole of that
 * judgement is `model/setTableNavigation.ts`; `lib/setTableArrows.ts` binds it here, once, at the
 * table rather than at each of the sixty inputs a day can hold.
 *
 * The gallery builds this as a CSS grid; `table-layout: fixed` over a `<colgroup>` reproduces the
 * same fixed tracks without trading the semantics away — see the stylesheet's header for why
 * `display: contents` was not an option.
 *
 * ── The three row actions are one store vocabulary ───────────────────────────────────────────
 * "Duplikuj" and "Wstaw powyżej" are both `insertSetBelow` — the operation copies the row above the
 * insertion point, so inserting below row N and below row N−1 are the two useful placements and
 * neither needs a second operation. "Usuń" is guarded: an exercise always keeps one set, because
 * `PostExerciseTemplateInput.Sets` is `[MinLength(1)]`.
 *
 * A fourth glyph opened a PER-SET NOTE until 3 August 2026. `Set` has no note column on the wire
 * (gap G4), so the field never survived a reload; the owner removed the affordance rather than keep
 * a control that quietly discarded what was typed into it. `SetDraft.note` and the mapper's drop of
 * it stay exactly as they were — `draftToRequest.test.ts` still pins that the wire carries no trace
 * of the field — so the day the backend grows the column the surface is one component away.
 *
 * ── "+ Parametr" is where columns are opened and closed ──────────────────────────────────────
 * The gallery puts the panel behind the last header cell, and this is its only home. It used to be
 * two groups inside the exercise's ⋯; carrying it in both would be two paths to one act.
 */
import { useState } from 'react';

import { Menu } from '@base-ui/react/menu';

import {
  hideColumn,
  insertSetBelow,
  removeSet,
  revealColumn,
  setPrescriptionMode,
  toggleRestColumn,
  PRESCRIPTION_MODE,
  type SetAddress,
  type SetGridFieldKey,
} from '~/entities/plan-draft';
import { ConfirmDialog, type ConfirmPrompt } from '~/shared/ui/ConfirmDialog';
import { HintPopover } from '~/shared/ui/HintPopover';

import { moveFocusOnArrow, setCellAddress } from '../lib/setTableArrows';
import { useDepartures } from '../lib/useDepartures';
import { ROW_OUT_MS } from '../model/exitTiming';
import type {
  ColumnView,
  ExerciseView,
  ParameterToggleView,
  SetRowView,
} from '../model/dayProgramming';
import type { ChangeCellView, ChangeRowView } from '../model/exerciseChange';

import { SetCell } from './SetCell';
import { SetTypeBadge, SetTypeMenu } from './SetTypeMenu';
import menuStyles from '~/shared/ui/MenuSurface.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import styles from './SetTable.module.css';

interface SetTableProps {
  readonly exercise: ExerciseView;
  readonly dayRestSeconds: number;
  readonly labelledBy: string;
  readonly announce: (message: string) => void;
  /** The day is being READ. Every act on the table goes; the prescription stays. */
  readonly readOnly: boolean;
  /**
   * A change to this exercise under review (board U): these rows are drawn instead of the
   * exercise's own, as text, with what the change moves marked. `null` everywhere a day is
   * programmed or read. The table's columns, header and geometry stay the builder's.
   */
  readonly changes: readonly ChangeRowView[] | null;
}

const TRACK_CLASS: Record<ColumnView['track'], string> = {
  reps: 'colReps',
  weight: 'colWeight',
  score: 'colScore',
  rest: 'colRest',
  extra: 'colExtra',
};

/** The gallery's three row glyphs, at its own 13px stroke weight. */
function RowIcon({ shape }: { readonly shape: 'duplicate' | 'remove' }) {
  return (
    <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {shape === 'duplicate' && (
        <>
          <rect x="9" y="9" width="11" height="11" rx="2" />
          <path d="M5 15V6a2 2 0 0 1 2-2h9" />
        </>
      )}
      {shape === 'remove' && <path d="M5 7h14M10 7V5h4v2M8 7l1 13h6l1-13" />}
    </svg>
  );
}

/** One control that may refuse, with the reason left reachable rather than removed. */
function RowButton({
  label,
  reason,
  shape,
  onClick,
}: {
  readonly label: string;
  readonly reason: string | null;
  readonly shape: 'duplicate' | 'remove';
  readonly onClick: () => void;
}) {
  const refused = reason !== null;
  return (
    <button
      type="button"
      className={styles.rowButton}
      aria-label={refused ? `${label}, ${reason}` : label}
      aria-disabled={refused ? 'true' : undefined}
      // `title` carries the reason for a pointer user; the accessible NAME carries it for everyone
      // else, because a `title` is not reliably announced and an icon button has no other text.
      title={reason ?? label}
      onClick={() => {
        if (!refused) onClick();
      }}
    >
      <RowIcon shape={shape} />
    </button>
  );
}

function Row({
  row,
  rowIndex,
  exercise,
  dayRestSeconds,
  isEntering,
  isDeparting,
  announce,
  readOnly,
}: {
  readonly row: SetRowView;
  /**
   * The row's place in the ARROW GRID, which is not `setIndex`: that one is 1-based and is the
   * number the trainer reads in the `#` column. Zero-based here, so the two never have to be
   * reconciled inside the navigation.
   */
  readonly rowIndex: number;
  readonly exercise: ExerciseView;
  readonly dayRestSeconds: number;
  /** Just added by "+ Seria" — it plays the canon's drop-in and green flash once. */
  readonly isEntering: boolean;
  /**
   * Already removed from the plan, and still on screen for the length of `rowOut`.
   *
   * It is drawn from the view it last had — see `model/departures` — and it is `inert`, because a
   * row nobody can reach any more must not hold a tab stop or answer a press. `readOnly` with it, so
   * nothing inside it reads the store about a set that is gone.
   */
  readonly isDeparting: boolean;
  readonly announce: (message: string) => void;
  readonly readOnly: boolean;
}) {
  const address: SetAddress = { exerciseSlotToken: exercise.slotToken, setIndex: row.setIndex };
  const span = row.cells.length + 3;

  return (
    <>
      <tr
        className={isDeparting ? styles.rowLeaving : (isEntering ? styles.rowEntering : undefined)}
        aria-hidden={isDeparting ? 'true' : undefined}
        inert={isDeparting}
      >
        <th scope="row" className={styles.indexCell}>
          {row.setIndex}
        </th>
        <td className={styles.typeCell}>
          <SetTypeMenu
            badge={row.badge}
            choices={row.typeChoices}
            address={address}
            menuLabel={row.actionsLabel}
            menuTitle={row.typeMenuTitle}
            announce={announce}
            readOnly={readOnly}
          />
        </td>

        {/* The cell publishes its address for the table's arrow handler to find it by. On the
          * `<td>` rather than inside `SetCell`, because navigation is the TABLE's concern: a cell
          * that had to be told where it sits would carry two props for a behaviour it does not
          * implement. */}
        {row.cells.map((cell, columnIndex) => (
          <td key={cell.key} data-set-cell={setCellAddress(rowIndex, columnIndex)}>
            <SetCell
              cell={cell}
              address={address}
              dayRestSeconds={dayRestSeconds}
              readOnly={readOnly}
            />
          </td>
        ))}

        <td className={styles.actionsCell}>
          {/* The cell itself stays in every mode: it is one of the columns every `colSpan` in this
            * table counts, and dropping it in the viewer would misalign the warning rows and the
            * add-set row against the header. What goes is the two acts inside it. */}
          {readOnly ? null : (
          <span className={styles.rowControls}>
            <RowButton
              label={row.duplicate.label}
              reason={row.duplicate.disabledReason}
              shape="duplicate"
              onClick={() => {
                insertSetBelow(exercise.slotToken, row.setIndex);
                announce(row.duplicate.label);
              }}
            />
            <RowButton
              label={row.remove.label}
              reason={row.remove.disabledReason}
              shape="remove"
              onClick={() => {
                removeSet(address);
                announce(row.remove.label);
              }}
            />
          </span>
          )}
        </td>
      </tr>

      {row.warning !== null && (
        <tr className={styles.warningRow}>
          <td colSpan={span}>
            <span className={styles.warning} role="status">
              {row.warning}
            </span>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * One value of a change under review: the old one struck beside the new, both said in words. A
 * value written into an empty cell stands alone in full ink, as a new set does; a struck dash in
 * front of it would read as a minus. The new value and its unit wrap as one, under the old one.
 */
function ChangeCell({ cell }: { readonly cell: ChangeCellView }) {
  const value = (
    <>
      {cell.previous !== null && <s className={styles.previousValue}>{cell.previous}</s>}
      {cell.text !== '' && (
        <span className={styles.nowValue} data-new={cell.isNew ? '' : undefined}>
          {cell.text}
          {cell.suffix !== null && <span className={styles.suffix}>{cell.suffix}</span>}
        </span>
      )}
    </>
  );
  if (cell.spoken === null) return <span className={styles.changeValue}>{value}</span>;
  return (
    <>
      <span className={styles.changeValue} aria-hidden="true">
        {value}
      </span>
      <span className={hidden.srOnly}>{cell.spoken}</span>
    </>
  );
}

/** A set as a change under review draws it: kept, added („+ 5") or taken away (struck). */
function ChangeRow({ row }: { readonly row: ChangeRowView }) {
  return (
    <tr data-change={row.change === 'kept' ? undefined : row.change}>
      <th scope="row" className={styles.indexCell}>
        <span aria-hidden="true">{row.index}</span>
        <span className={hidden.srOnly}>{row.spoken === null ? row.label : `${row.label}, ${row.spoken}`}</span>
      </th>
      <td className={styles.typeCell}>
        <span className={styles.rowControls}>
          {row.previousBadge !== null && (
            <s className={styles.previousValue} aria-hidden="true">
              {row.previousBadge.letter}
            </s>
          )}
          <SetTypeBadge badge={row.badge} />
          {row.typeSpoken !== null && <span className={hidden.srOnly}>{row.typeSpoken}</span>}
        </span>
      </td>
      {row.cells.map((cell) => (
        <td key={cell.key}>
          <ChangeCell cell={cell} />
        </td>
      ))}
    </tr>
  );
}

/** The "Parametry serii" panel behind the "+ Parametr" header cell. */
function ParametersMenu({
  menu,
  onToggle,
}: {
  readonly menu: ExerciseView['columnMenu'];
  readonly onToggle: (entry: ParameterToggleView) => void;
}) {
  return (
    <Menu.Root>
      {/* The canon prints a bare `+` in the 40px trailing cell — "+ Parametr" does not fit in it and
        * was clipping. The WORDS are the accessible name, so a screen reader and voice control still
        * get "Parametry serii" while the header keeps the canon's glyph; `menu.triggerLabel` remains
        * the popup's own heading. */}
      <Menu.Trigger className={styles.parameters} aria-label={menu.title}>
        <span aria-hidden="true">+</span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="end">
          <Menu.Popup
            className={`${String(menuStyles.popup)} ${String(styles.parameterPopup)}`}
            aria-label={menu.title}
          >
            <span className={menuStyles.groupLabel}>{menu.title}</span>
            {menu.toggles.map((entry) => (
              <Menu.CheckboxItem
                key={entry.field}
                className={menuStyles.item}
                checked={entry.isOn}
                /* The panel stays open for an ordinary toggle — a trainer opening three columns
                 * should not have to re-open it twice. It CLOSES for the one that raises a confirm:
                 * Base UI's menu is modal, so leaving it up marks the rest of the document inert and
                 * the dialog it just raised cannot be answered. Found by driving the suite. */
                closeOnClick={entry.isOn && entry.confirm !== null}
                // `aria-disabled` rather than `disabled`: a locked parameter carries the reason it
                // is locked, and the attribute would drop it out of the roving sequence with the
                // explanation inside it. The reason is DRAWN under the label — see below.
                aria-disabled={entry.lockedReason !== null ? 'true' : undefined}
                onCheckedChange={() => {
                  onToggle(entry);
                }}
              >
                {/* A tick AND the pressed state Base UI writes as `aria-checked`: the mark is a
                  * second channel rather than the only one. */}
                <span className={menuStyles.mark} aria-hidden="true">
                  {entry.isOn ? '\u2713' : ''}
                </span>
                {/* The reason under the label, exactly as `SessionMenu`'s entries draw theirs. It
                  * was carried by nothing but `aria-disabled` until 7 September 2026, so a trainer
                  * met an entry that refused to tick and said why nowhere. The `hint` beside it is
                  * the column's SHAPE — "kg lub % 1RM", "3-1-1" — and reps dropped its own the same
                  * day: one row saying "required" twice in two registers is a statement, not a
                  * shape. */}
                <span className={menuStyles.itemLabel}>
                  {entry.label}
                  {entry.lockedReason !== null && (
                    <span className={menuStyles.reason}>{entry.lockedReason}</span>
                  )}
                </span>
                {entry.hint !== null && <span className={menuStyles.itemHint}>{entry.hint}</span>}
              </Menu.CheckboxItem>
            ))}
            <span className={menuStyles.footnote}>{menu.hint}</span>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

/** What a pending column removal is waiting to be told. */
interface PendingRemoval {
  readonly field: SetGridFieldKey;
  readonly label: string;
  readonly confirm: ConfirmPrompt;
}

export function SetTable({
  exercise,
  dayRestSeconds,
  labelledBy,
  announce,
  readOnly,
  changes,
}: SetTableProps) {
  // The column whose removal is waiting on an answer. Held here because both controls that can ask
  // it — the header's own `×` and the "Parametry serii" checkbox — are inside this table, and one
  // dialog for one question is what keeps the two of them a single act.
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);

  /**
   * Which row the canon's entrance is playing on.
   *
   * Derived during render from the row count moving, which is React's own documented way of
   * adjusting state on a prop change — and the only one available here: a ref written in render is
   * banned by the compiler, and an effect would run a frame after the row is already painted in
   * place, so the animation would start from where it was meant to end.
   *
   * It is never cleared, and it does not need to be: the keyframe's `both` fill leaves the row at
   * exactly its resting appearance, and a CSS animation replays only when the class is applied to a
   * newly matched element. The next "+ Seria" moves the key and the previous row stops being it.
   */
  // The rows to draw: the exercise's own, plus any the trainer just deleted, held in place for the
  // length of `rowOut`. The removal already reached the store — see `model/departures`.
  const slots = useDepartures(exercise.rows, ROW_OUT_MS, exercise.slotToken);

  const [seenRowCount, setSeenRowCount] = useState(exercise.rows.length);
  const [enteringKey, setEnteringKey] = useState<string | null>(null);
  if (seenRowCount !== exercise.rows.length) {
    setEnteringKey(
      exercise.rows.length > seenRowCount
        ? (exercise.rows[exercise.rows.length - 1]?.key ?? null)
        : null,
    );
    setSeenRowCount(exercise.rows.length);
  }

  // Rest is not a measurement field — it has its own flag and its own restamp back to the day's
  // value — so both directions route it to the toggle and everything else to the column operations.
  const openColumn = (field: SetGridFieldKey) => {
    if (field === 'rest') toggleRestColumn(exercise.slotToken);
    else revealColumn(exercise.slotToken, field);
  };
  const closeColumn = (field: SetGridFieldKey, label: string) => {
    if (field === 'rest') toggleRestColumn(exercise.slotToken);
    else hideColumn(exercise.slotToken, field);
    announce(label);
  };

  /** A column with values behind it asks first; an empty one has nothing to lose. */
  const requestClose = (field: SetGridFieldKey, label: string, prompt: ConfirmPrompt | null) => {
    if (prompt === null) {
      closeColumn(field, label);
      return;
    }
    setPendingRemoval({ field, label, confirm: prompt });
  };

  const toggleParameter = (entry: ParameterToggleView) => {
    if (entry.lockedReason !== null) {
      announce(entry.lockedReason);
      return;
    }
    if (entry.isOn) {
      requestClose(entry.field, entry.label, entry.confirm);
      return;
    }
    openColumn(entry.field);
    announce(entry.label);
  };

  if (exercise.noColumns !== null) {
    // A short statement, and the reason one press away. It was a full explanatory sentence inside
    // the card until 7 September 2026 — the owner's standing rule is that an explanation lives
    // under an ⓘ or it goes.
    //
    // The ⓘ is a CONTROL, so the read-only viewer keeps the statement and loses the trigger, like
    // every other press on that surface.
    return (
      <p className={styles.noColumns}>
        {exercise.noColumns.label}
        {!readOnly && (
          <HintPopover label={exercise.noColumns.hintLabel}>{exercise.noColumns.hint}</HintPopover>
        )}
      </p>
    );
  }

  return (
    <div className={styles.tableWrap}>
      <table
        className={styles.table}
        data-changes={changes === null ? undefined : ''}
        aria-labelledby={labelledBy}
        // One listener for every cell in the table — see `lib/setTableArrows.ts`. It runs on the
        // BUBBLE, so `SetCell`'s own Enter and Escape have already been answered by the time it
        // sees a key: the value is committed before the caret moves, and Escape never arrives at
        // all. It answers the four arrows, Enter and Tab, and nothing else.
        onKeyDown={(event) => {
          moveFocusOnArrow(event, {
            rowCount: exercise.rows.length,
            columnCount: exercise.columns.length,
          });
        }}
      >
        <colgroup>
          <col className={styles.colIndex} />
          <col className={styles.colType} />
          {exercise.columns.map((column) => (
            <col key={column.key} className={styles[TRACK_CLASS[column.track]]} />
          ))}
          {changes === null && <col className={styles.colActions} />}
        </colgroup>
        <thead>
          <tr>
            {/* `aria-label` rather than a hidden span: it IS the cell's accessible name, so a
              * screen reader gets the words while the gallery keeps its `#`. */}
            <th scope="col" aria-label={exercise.setColumnLabel}>
              #
            </th>
            <th scope="col">{exercise.typeColumnLabel}</th>
            {exercise.columns.map((column) => (
              <th key={column.key} scope="col">
                <span className={styles.headInner}>
                  {column.label}
                  {column.key === 'weight' && exercise.unit !== null && !readOnly && (
                    <button
                      type="button"
                      className={styles.unitButton}
                      aria-label={exercise.unit.toggleLabel}
                      onClick={() => {
                        const next = exercise.isPercentMode
                          ? PRESCRIPTION_MODE.absolute
                          : PRESCRIPTION_MODE.percent;
                        setPrescriptionMode(exercise.slotToken, next);
                        announce(exercise.unit?.toggleLabel ?? '');
                      }}
                    >
                      {exercise.unit.label}
                      <span aria-hidden="true">▾</span>
                    </button>
                  )}
                  {/* The unit is not decoration on this column: 82,5 means one thing in kilograms
                    * and another as a percentage of a maximum. With the toggle gone the word has to
                    * stay, so the viewer prints it where the control stood. */}
                  {column.key === 'weight' && exercise.unit !== null && readOnly && (
                    <span className={styles.unitStatic}>{exercise.unit.label}</span>
                  )}
                  {column.close !== null && !readOnly && (
                    <ColumnClose column={column} onClose={requestClose} />
                  )}
                </span>
              </th>
            ))}
            {changes === null && (
              <th scope="col" aria-label={exercise.actionsColumnLabel}>
                {readOnly ? null : (
                  <ParametersMenu menu={exercise.columnMenu} onToggle={toggleParameter} />
                )}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {changes?.map((row) => <ChangeRow key={row.key} row={row} />)}
          {changes === null && slots.map((slot, rowIndex) => (
            <Row
              key={slot.key}
              row={slot.item}
              rowIndex={rowIndex}
              exercise={exercise}
              dayRestSeconds={dayRestSeconds}
              isEntering={slot.key === enteringKey}
              isDeparting={slot.isDeparting}
              announce={announce}
              readOnly={readOnly || slot.isDeparting}
            />
          ))}
          {readOnly || changes !== null ? null : (
          <tr className={styles.addRow}>
            <td colSpan={exercise.columns.length + 3}>
              <span className={styles.addInner}>
                <button
                  type="button"
                  className={styles.addSet}
                  // The refusal in the NAME as well as on `title` — same pattern as `RowButton`
                  // above, and for the same reason: a `title` reaches a pointer and nobody else.
                  // The name contains the visible words, which WCAG 2.5.3 requires.
                  aria-label={
                    exercise.addSet.disabledReason === null
                      ? undefined
                      : `${exercise.addSet.label}, ${exercise.addSet.disabledReason}`
                  }
                  aria-disabled={exercise.addSet.disabledReason !== null ? 'true' : undefined}
                  title={exercise.addSet.disabledReason ?? exercise.addSet.label}
                  onClick={() => {
                    if (exercise.addSet.disabledReason !== null) return;
                    insertSetBelow(exercise.slotToken, exercise.rows.length);
                    announce(exercise.addSet.label);
                  }}
                >
                  {/* The label IS "+ Seria" — the mock's own words, glyph included — so the button
                    * draws no second `+`. It carried one until 8 September 2026 and read "+ + Seria". */}
                  {exercise.addSet.label}
                </button>
              </span>
            </td>
          </tr>
          )}
        </tbody>
      </table>

      <ConfirmDialog
        prompt={pendingRemoval?.confirm ?? null}
        onConfirm={() => {
          const column = pendingRemoval;
          setPendingRemoval(null);
          if (column !== null) closeColumn(column.field, column.label);
        }}
        onCancel={() => {
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}

/** A column the trainer ADDED, with the close the gallery draws in its own header. */
function ColumnClose({
  column,
  onClose,
}: {
  readonly column: ColumnView;
  readonly onClose: (field: SetGridFieldKey, label: string, prompt: ConfirmPrompt | null) => void;
}) {
  const close = column.close;
  if (close === null) return null;
  return (
    <button
      type="button"
      className={styles.columnClose}
      aria-label={close.label}
      onClick={() => {
        onClose(close.field, close.label, close.confirm);
      }}
    >
      <span aria-hidden="true">×</span>
    </button>
  );
}
